import { Check, ChevronDown, Search, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { cn } from "@/shared/lib/cn";
import { InvitedBadge } from "@/shared/membership/invited-badge";

export interface UserPickerOption {
  userId: string;
  displayName: string;
  email: string;
  avatarUrl?: string | null;
  /** True when this person is invited but has not accepted yet (pending member). */
  pending?: boolean;
}

interface Props {
  value: string | null | undefined;
  onChange: (userId: string | null) => void;
  options: ReadonlyArray<UserPickerOption>;
  /** Label on the trigger when no user is selected. */
  emptyLabel?: string;
  /** Search-input placeholder text. */
  searchPlaceholder?: string;
  /** Trigger className for sizing/style. */
  className?: string;
  /** When true, the trigger renders compactly (toolbar usage). */
  compact?: boolean;
  /** Optional aria-label for the trigger when the visible label is just an
   *  icon. */
  ariaLabel?: string;
  /** Optional id for label association (`<Label htmlFor>`). Lands on the
   *  trigger button, which is the control a label should point at. Mirrors
   *  `DatePicker`, and lets `<FormControl>` (a Slot) reach a real control. */
  id?: string;
  /** Fallback descriptor used to render the trigger when `value` is set but
   *  `options` hasn't loaded yet (e.g. presets opened over a still-loading
   *  people list). Pure presentation, doesn't extend the searchable list. */
  selectedFallback?: UserPickerOption;
}

/**
 * Shared user-picker. Renders a popover with a live search input + scrollable
 * list of avatar / displayName / email rows. The trigger collapses to the
 * selected user (avatar + name) or the `emptyLabel`. Pass the full list of
 * candidate users via `options`, the picker handles filtering, keyboard
 * focus, and clearing locally.
 */
export function UserPicker({
  value,
  onChange,
  options,
  emptyLabel = "Anyone",
  searchPlaceholder = "Search by name or email…",
  className,
  compact = false,
  ariaLabel,
  selectedFallback,
  id,
}: Props): ReactNode {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => {
    if (!value) return undefined;
    const hit = options.find((o) => o.userId === value);
    if (hit) return hit;
    if (selectedFallback && selectedFallback.userId === value) return selectedFallback;
    return undefined;
  }, [value, options, selectedFallback]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = [...options].sort((a, b) => a.displayName.localeCompare(b.displayName));
    if (!needle) return sorted;
    return sorted.filter(
      (o) => o.displayName.toLowerCase().includes(needle) || o.email.toLowerCase().includes(needle),
    );
  }, [options, q]);

  // Reset search + focus the input when the popover opens.
  useEffect(() => {
    if (open) {
      setQ("");
      // Defer to next tick so Radix has mounted the content.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          size={compact ? "sm" : "default"}
          className={cn(
            "justify-between gap-2 font-normal",
            compact ? "h-8 px-2 text-xs" : "",
            className,
          )}
          aria-label={ariaLabel}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            {selected ? (
              <>
                <Avatar className="size-5 shrink-0">
                  <AvatarImage src={selected.avatarUrl ?? undefined} alt="" />
                  <AvatarFallback className="bg-pulse-soft text-3xs text-text-brand-strong">
                    {initials(selected.displayName)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{selected.displayName}</span>
                {selected.pending ? <InvitedBadge /> : null}
              </>
            ) : (
              <span className="truncate text-text-tertiary">{emptyLabel}</span>
            )}
          </span>
          <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex max-h-[360px] w-72 flex-col overflow-hidden bg-card p-0 shadow-lg"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
          <Search aria-hidden="true" className="size-3.5 shrink-0 text-text-tertiary" />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-7 border-0 px-0 text-sm shadow-none focus-visible:ring-0"
          />
          {q ? (
            <button
              type="button"
              onClick={() => setQ("")}
              className="inline-flex size-5 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-surface-subtle hover:text-text-primary"
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          <PickerRow
            avatar={null}
            displayName={emptyLabel}
            email=""
            selected={!value}
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          />
          {filtered.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-text-tertiary">No matches</p>
          ) : (
            filtered.map((o) => (
              <PickerRow
                key={o.userId}
                avatar={o.avatarUrl ?? null}
                displayName={o.displayName}
                email={o.email}
                pending={o.pending}
                selected={o.userId === value}
                onClick={() => {
                  onChange(o.userId);
                  setOpen(false);
                }}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface PickerRowProps {
  avatar: string | null;
  displayName: string;
  email: string;
  pending?: boolean;
  selected: boolean;
  onClick: () => void;
}

function PickerRow({
  avatar,
  displayName,
  email,
  pending,
  selected,
  onClick,
}: PickerRowProps): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors",
        "hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-none",
        selected ? "bg-surface-subtle" : "",
      )}
    >
      {avatar !== null || displayName ? (
        <Avatar className="size-7 shrink-0">
          <AvatarImage src={avatar ?? undefined} alt="" />
          <AvatarFallback className="bg-pulse-soft text-3xs text-text-brand-strong">
            {initials(displayName)}
          </AvatarFallback>
        </Avatar>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-text-primary">{displayName}</span>
          {pending ? <InvitedBadge /> : null}
        </span>
        {email ? <span className="truncate text-xs text-text-tertiary">{email}</span> : null}
      </span>
      {selected ? <Check aria-hidden="true" className="size-3.5 text-text-brand" /> : null}
    </button>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]?.charAt(0).toUpperCase() ?? "?";
  return `${parts[0]?.charAt(0) ?? ""}${parts[parts.length - 1]?.charAt(0) ?? ""}`.toUpperCase();
}
